/**
 * 진단 로그 보기 (도움말 > 로그 보기) — 스탠드얼론 전용.
 *
 * 스탠드얼론은 콘솔이 없어 로그가 원인 추적의 유일한 수단인데, 파일을 직접 찾아가게
 * 하면 경로부터 틀린다. 화면에서 바로 보고 **복사해서 리포팅**하게 한다.
 *
 * 파일 읽기는 main 프로세스가 한다(preload 의 electronAPI.diag). 렌더러는 경로를
 * 모르고 id 만 부른다 — 경로를 넘기면 임의 파일 읽기 통로가 된다.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { FiX, FiRefreshCw, FiCopy, FiFolder, FiAlertTriangle } from 'react-icons/fi';
import './LogViewerModal.css';

/** 바이트를 사람이 읽는 크기로. */
function fmtSize(n) {
    if (!n) return '0 B';
    const u = ['B', 'KB', 'MB', 'GB'];
    let v = n, i = 0;
    while (v >= 1024 && i < u.length - 1) { v /= 1024; i++; }
    return `${v.toFixed(v >= 10 || i === 0 ? 0 : 1)} ${u[i]}`;
}

const LogViewerModal = ({ isOpen, onClose }) => {
    const { t } = useTranslation();

    const [logs, setLogs] = useState([]);
    const [selectedId, setSelectedId] = useState(null);
    const [content, setContent] = useState(null);   // { text, size, truncated } | null
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [env, setEnv] = useState(null);
    const [onlyProblems, setOnlyProblems] = useState(false);

    const diag = typeof window !== 'undefined' ? window.electronAPI?.diag : null;

    const loadList = useCallback(async () => {
        if (!diag) return;
        try {
            const list = await diag.listLogs();
            setLogs(list || []);
            // 기본 선택은 **내용이 있는 첫 로그** — 빈 파일을 먼저 보여주면 "로그가 없다" 로 오해한다.
            setSelectedId(prev => prev || (list.find(l => l.exists && l.size > 0) || list[0])?.id || null);
        } catch (e) {
            setError(t('common:logViewer.err.list'));
        }
    }, [diag, t]);

    const loadContent = useCallback(async (id) => {
        if (!diag || !id) return;
        setLoading(true);
        setError(null);
        try {
            const res = await diag.readLog(id);
            if (res?.ok) {
                setContent(res);
            } else {
                setContent(null);
                setError(res?.reason === 'NOT_FOUND'
                    ? t('common:logViewer.err.notFound')
                    : t('common:logViewer.err.read', { detail: res?.detail || res?.reason || '' }));
            }
        } catch (e) {
            setContent(null);
            setError(t('common:logViewer.err.read', { detail: e.message }));
        } finally {
            setLoading(false);
        }
    }, [diag, t]);

    useEffect(() => {
        if (!isOpen || !diag) return;
        loadList();
        diag.env().then(setEnv).catch(() => { /* 머리말은 없어도 로그는 봐야 한다 */ });
    }, [isOpen, diag, loadList]);

    useEffect(() => {
        if (isOpen && selectedId) loadContent(selectedId);
    }, [isOpen, selectedId, loadContent]);

    const selected = logs.find(l => l.id === selectedId);

    // 문제만 보기 — 긴 로그에서 ERR/실패 줄만 추린다.
    const shownText = useMemo(() => {
        const raw = content?.text || '';
        if (!onlyProblems) return raw;
        const hit = /(\bERR\b|ERROR|RENDERER_ERROR|RENDERER_GONE|RENDERER_LOAD_FAIL|PRELOAD_ERROR|UNCAUGHT|UNHANDLED|ORA-\d+|Failed|❌)/i;
        const lines = raw.split('\n').filter(l => hit.test(l));
        return lines.length > 0 ? lines.join('\n') : '';
    }, [content, onlyProblems]);

    // 리포팅용 — 버전·플랫폼 머리말을 붙여 통째로 복사한다.
    const handleCopy = useCallback(async () => {
        const head = env
            ? [
                `LiveSQL ${env.appVersion} (electron ${env.electron}, node ${env.node})`,
                `${env.platform} / ${env.osRelease}`,
                `userData: ${env.userData}`,
                selected ? `log: ${selected.path}` : '',
                content?.truncated ? t('common:logViewer.tailNote', { size: fmtSize(content.size) }) : '',
                '---',
            ].filter(Boolean).join('\n')
            : '';
        try {
            await navigator.clipboard.writeText(`${head}\n${shownText}`);
            toast.success(t('common:logViewer.copied'));
        } catch {
            toast.error(t('common:logViewer.err.copy'));
        }
    }, [env, selected, content, shownText, t]);

    const handleReveal = useCallback(async () => {
        if (!diag || !selectedId) return;
        const res = await diag.revealLog(selectedId);
        if (!res?.ok) toast.error(t('common:logViewer.err.reveal'));
    }, [diag, selectedId, t]);

    if (!isOpen) return null;

    return (
        <div className="logv-overlay" onClick={onClose}>
            <div className="logv-modal" onClick={(e) => e.stopPropagation()}>
                <div className="logv-header">
                    <h2>{t('common:logViewer.title')}</h2>
                    <button className="logv-close" onClick={onClose} title={t('common:ui.close')}>
                        <FiX size={18} />
                    </button>
                </div>

                {!diag ? (
                    // 웹에서는 로컬 로그가 없다 — 메뉴에서도 숨기지만, 직접 열렸을 때를 대비한다.
                    <div className="logv-body">
                        <p className="logv-empty">{t('common:logViewer.standaloneOnly')}</p>
                    </div>
                ) : (
                    <>
                        <div className="logv-toolbar">
                            <select
                                className="logv-select"
                                value={selectedId || ''}
                                onChange={(e) => setSelectedId(e.target.value)}
                            >
                                {logs.map(l => (
                                    <option key={l.id} value={l.id}>
                                        {t(`common:logViewer.${l.labelKey}`)}
                                        {l.exists ? ` — ${fmtSize(l.size)}` : ` — ${t('common:logViewer.missing')}`}
                                    </option>
                                ))}
                            </select>

                            <label className="logv-check">
                                <input
                                    type="checkbox"
                                    checked={onlyProblems}
                                    onChange={(e) => setOnlyProblems(e.target.checked)}
                                />
                                {t('common:logViewer.onlyProblems')}
                            </label>

                            <div className="logv-spacer" />

                            <button className="logv-btn" onClick={() => loadContent(selectedId)} disabled={loading}>
                                <FiRefreshCw size={13} /> {t('common:logViewer.refresh')}
                            </button>
                            <button className="logv-btn" onClick={handleReveal} disabled={!selected?.exists}>
                                <FiFolder size={13} /> {t('common:logViewer.openFolder')}
                            </button>
                            <button className="logv-btn primary" onClick={handleCopy} disabled={!shownText}>
                                <FiCopy size={13} /> {t('common:logViewer.copyForReport')}
                            </button>
                        </div>

                        {selected && (
                            <div className="logv-path" title={selected.path}>{selected.path}</div>
                        )}

                        <div className="logv-body">
                            {error && (
                                <p className="logv-error">
                                    <FiAlertTriangle size={13} /> {error}
                                </p>
                            )}
                            {!error && loading && <p className="logv-empty">{t('common:logViewer.loading')}</p>}
                            {!error && !loading && !shownText && (
                                <p className="logv-empty">
                                    {onlyProblems ? t('common:logViewer.noProblems') : t('common:logViewer.empty')}
                                </p>
                            )}
                            {!error && !loading && shownText && (
                                <>
                                    {content?.truncated && !onlyProblems && (
                                        <p className="logv-note">
                                            {t('common:logViewer.tailNote', { size: fmtSize(content.size) })}
                                        </p>
                                    )}
                                    <pre className="logv-pre">{shownText}</pre>
                                </>
                            )}
                        </div>

                        <div className="logv-footer">
                            <span className="logv-hint">{t('common:logViewer.reportHint')}</span>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

LogViewerModal.propTypes = {
    isOpen: PropTypes.bool,
    onClose: PropTypes.func.isRequired,
};

export default LogViewerModal;
